// src/pages/storePages/StoresPage.js
import { useEffect, useRef, useState } from "react";
import { storeGetCategoryTree } from "../../storeAPIs/StoreCategoriesApis";
import { storeGetAllProducts } from "../../storeAPIs/StoreProductsAPIs";
import ProductCard from "../../components/store/ProductCard";
import "../../static/storecss/StoresPage.css";
import Footer from "../../components/common/Footer";
import SITE from "../../config/site";

// Touch-only detection — true on phones/tablets without a mouse
const isTouchOnly = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: none)").matches;

export default function StoresPage() {
  const [tree, setTree] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedName, setSelectedName] = useState("All Products");
  const [products, setProducts] = useState([]);
  const [loadingTree, setLoadingTree] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [openRootSlug, setOpenRootSlug] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const closeTimer = useRef(null);

  // ── Load category tree ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    storeGetCategoryTree()
      .then((res) => {
        if (!cancelled) setTree(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load categories.");
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Reset page when category changes ───────────────────────────────
  useEffect(() => {
    setPage(1);
  }, [selectedCategory]);

  // ── Load products ──────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const isFirstPage = page === 1;

    if (isFirstPage) {
      setLoadingProducts(true);
      setProducts([]);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    const params = { page };
    if (selectedCategory) params.category = selectedCategory;

    storeGetAllProducts(params)
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? {};
        const results = data.results ?? data ?? [];
        setProducts((prev) => (isFirstPage ? results : [...prev, ...results]));
        setHasNext(Boolean(data.next));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Failed to load products.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingProducts(false);
        setLoadingMore(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCategory, page]);

  // ── Escape closes the drawer ───────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Sidebar helpers ────────────────────────────────────────────────
  const openRoot = (slug) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenRootSlug(slug);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenRootSlug(null), 200);
  };

  const handleSelect = (slug, name) => {
    setSelectedCategory(slug);
    setSelectedName(name);
    setOpenRootSlug(null);
    setIsDrawerOpen(false);
  };

  const handleRootClick = (root) => {
    const hasChildren = root.children?.length > 0;

    if (hasChildren) {
      setOpenRootSlug((prev) => (prev === root.slug ? null : root.slug));
      return;
    }

    handleSelect(root.slug, root.name);
  };

  const visibleProducts = query.trim()
    ? products.filter((p) =>
        p.name.toLowerCase().includes(query.trim().toLowerCase())
      )
    : products;

  return (
    <div className="st-pg-page">
      {/* ── TOP BAR ── */}
      <div className="st-pg-topbar">
        <div className="st-pg-topbar-inner">
          <div className="st-pg-brand">
            <button
              type="button"
              className="st-pg-drawer-toggle"
              onClick={() => setIsDrawerOpen((v) => !v)}
              aria-label="Toggle categories"
              aria-expanded={isDrawerOpen}
            >
              <span className="st-pg-hamburger-line" />
              <span className="st-pg-hamburger-line" />
              <span className="st-pg-hamburger-line" />
            </button>

            <span className="st-pg-brand-mark">
              <i className="fa-solid fa-store" />
            </span>
            <span className="st-pg-brand-text">
              <span className="st-pg-brand-name">Hayducate Store</span>
            </span>
          </div>

          <div className="st-pg-topbar-contact">
            <a
              className="st-pg-contact-chip"
              href={`tel:${SITE.contact.phoneRaw}`}
            >
              <i className="fa-solid fa-phone" />
              <span>{SITE.contact.phoneDisplay}</span>
            </a>
            <span className="st-pg-contact-divider" aria-hidden="true" />
            <a
              className="st-pg-contact-chip"
              href={`mailto:${SITE.contact.email}`}
            >
              <i className="fa-solid fa-envelope" />
              <span>{SITE.contact.email}</span>
            </a>

            <span className="st-pg-contact-divider" aria-hidden="true" />
            <div className="st-pg-social" aria-label="Social links">
              <a
                href={SITE.social.twitter}
                className="st-pg-social-link"
                aria-label="Twitter"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-x-twitter" />
              </a>
              <a
                href={SITE.social.facebook}
                className="st-pg-social-link"
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-facebook-f" />
              </a>
              <a
                href={SITE.social.linkedin}
                className="st-pg-social-link"
                aria-label="LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-linkedin-in" />
              </a>
              <a
                href={SITE.social.youtube}
                className="st-pg-social-link"
                aria-label="YouTube"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-youtube" />
              </a>
              <a
                href={SITE.social.tiktok}
                className="st-pg-social-link"
                aria-label="TikTok"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-tiktok" />
              </a>
              <a
                href={SITE.social.whatsapp}
                className="st-pg-social-link"
                aria-label="WhatsApp"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-whatsapp" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── BACKDROP (mobile only) ── */}
      <div
        className={`st-pg-drawer-backdrop ${
          isDrawerOpen ? "st-pg-drawer-backdrop-open" : ""
        }`}
        onClick={() => setIsDrawerOpen(false)}
        aria-hidden="true"
      />

      <div className="st-pg-layout">
        {/* ── SIDEBAR / DRAWER ── */}
        <aside
          className={`st-pg-sidebar ${
            isDrawerOpen ? "st-pg-sidebar-open" : ""
          } ${loadingTree ? "is-loading" : ""}`}
        >
          <div className="st-pg-sidebar-inner">
            <div className="st-pg-sidebar-header">
              <h3 className="st-pg-sidebar-title">Categories</h3>
              <button
                type="button"
                className="st-pg-drawer-close"
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close categories"
              >
                ✕
              </button>
            </div>

            <button
              type="button"
              className={`st-pg-cat-btn ${
                selectedCategory === null ? "st-pg-cat-btn-active" : ""
              }`}
              onClick={() => handleSelect(null, "All Products")}
            >
              <span className="st-pg-cat-icon">
                <i className="fa-solid fa-border-all" />
              </span>
              <span className="st-pg-cat-name">All Products</span>
            </button>

            {loadingTree ? (
              <div aria-hidden="true">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ height: 40, margin: "4px 0" }} />
                ))}
              </div>
            ) : (
              <ul className="st-pg-root-list" onMouseLeave={scheduleClose}>
                {tree.map((root) => {
                  const hasChildren = root.children?.length > 0;
                  const isOpen = openRootSlug === root.slug;

                  return (
                    <li
                      key={root.id}
                      className="st-pg-root-item"
                      onMouseEnter={() =>
                        hasChildren && !isTouchOnly() && openRoot(root.slug)
                      }
                    >
                      <button
                        type="button"
                        className={`st-pg-cat-btn ${
                          selectedCategory === root.slug
                            ? "st-pg-cat-btn-active"
                            : ""
                        }`}
                        onClick={() => handleRootClick(root)}
                        aria-expanded={hasChildren ? isOpen : undefined}
                      >
                        <span className="st-pg-cat-name">{root.name}</span>
                        {hasChildren && (
                          <i
                            className={`fa-solid fa-chevron-right st-pg-arrow ${
                              isOpen ? "st-pg-arrow-open" : ""
                            }`}
                          />
                        )}
                      </button>

                      {isOpen && hasChildren && (
                        <FlyoutColumn
                          nodes={root.children}
                          onSelect={(slug, name) => handleSelect(slug, name)}
                          selectedSlug={selectedCategory}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        <main className={`st-pg-main ${loadingProducts ? "is-loading" : ""}`}>
          <div className="st-pg-searchbar">
            <div className="st-pg-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="text"
                placeholder="Search products…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="st-pg-section-header">
            <h2>{selectedName}</h2>
            <span className="st-pg-count">
              {visibleProducts.length} items
            </span>
          </div>

          {error && <p className="st-pg-error">{error}</p>}

          {loadingProducts ? (
            <div
              className="st-pg-product-grid"
              aria-busy="true"
              aria-live="polite"
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="st-pg-skeleton"
                  aria-hidden="true"
                />
              ))}
            </div>
          ) : visibleProducts.length === 0 ? (
            <div className="st-pg-empty-state">
              <p>No products found.</p>
              {query && (
                <button onClick={() => setQuery("")}>Clear search</button>
              )}
            </div>
          ) : (
            <>
              <div className="st-pg-product-grid">
                {visibleProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {hasNext && !query && (
                <div className="st-pg-load-more-wrap">
                  <button
                    className="st-pg-load-more"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}

/* -----------------------------------------------------------------
   Recursive flyout column
----------------------------------------------------------------- */
function FlyoutColumn({ nodes, onSelect, selectedSlug }) {
  const [openChildId, setOpenChildId] = useState(null);
  const closeTimer = useRef(null);

  const openChild = (id) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpenChildId(id);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenChildId(null), 200);
  };

  const handleNodeClick = (node) => {
    const hasChildren = node.children?.length > 0;

    if (hasChildren) {
      setOpenChildId((prev) => (prev === node.id ? null : node.id));
      return;
    }

    onSelect(node.slug, node.name);
  };

  return (
    <div className="st-pg-flyout" onMouseLeave={scheduleClose}>
      <ul className="st-pg-flyout-list">
        {nodes.map((node) => {
          const hasChildren = node.children?.length > 0;
          const isOpen = openChildId === node.id;

          return (
            <li
              key={node.id}
              className="st-pg-flyout-item"
              onMouseEnter={() =>
                hasChildren && !isTouchOnly() && openChild(node.id)
              }
            >
              <button
                type="button"
                className={`st-pg-flyout-btn ${
                  selectedSlug === node.slug
                    ? "st-pg-flyout-btn-active"
                    : ""
                }`}
                onClick={() => handleNodeClick(node)}
                aria-expanded={hasChildren ? isOpen : undefined}
              >
                <span>{node.name}</span>
                {hasChildren && (
                  <i
                    className={`fa-solid fa-chevron-right st-pg-arrow ${
                      isOpen ? "st-pg-arrow-open" : ""
                    }`}
                  />
                )}
              </button>

              {hasChildren && isOpen && (
                <FlyoutColumn
                  nodes={node.children}
                  onSelect={onSelect}
                  selectedSlug={selectedSlug}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}